import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { fetchBrandsAction } from "../../../redux/slices/categories/brandsSlice";
import { fetchCategoriesAction } from "../../../redux/slices/categories/categoriesSlice";
import { createStyleAction, fetchStylesAction } from "../../../redux/slices/categories/stylesSlice";
import { createProductAction } from "../../../redux/slices/products/productSlices";
import { resetSuccessAction } from "../../../redux/slices/globalActions/globalActions";
import Swal from "sweetalert2";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";

const animatedComponents = makeAnimated();

// Size options for dropdown
const sizeOptions = [
  { value: "S", label: "Small (S)" },
  { value: "M", label: "Medium (M)" },
  { value: "L", label: "Large (L)" },
  { value: "XL", label: "Extra Large (XL)" },
  { value: "XXL", label: "Double Extra Large (XXL)" },
];

export default function AddProduct() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Files state
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [fileErrs, setFileErrs] = useState([]);

  // Size state - SINGLE selection
  const [selectedSize, setSelectedSize] = useState(null);

  const fileHandleChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const newErrs = [];
    const validFiles = [];
    const previews = [];

    if (files.length + newFiles.length > 5) {
      Swal.fire({
        icon: "warning",
        title: "Too Many Files",
        text: "You can upload maximum 5 images per product"
      });
      return;
    }

    newFiles.forEach((file) => {
      if (file?.size > 2 * 1024 * 1024) {
        newErrs.push(`${file?.name} is too large (max 2MB)`);
        return;
      }
      if (!file?.type?.startsWith("image/")) {
        newErrs.push(`${file?.name} is not an image`);
        return;
      }
      validFiles.push(file);
      const previewUrl = URL.createObjectURL(file);
      previews.push(previewUrl);
    });

    setFiles([...files, ...validFiles]);
    setFilePreviews([...filePreviews, ...previews]);
    setFileErrs([...fileErrs, ...newErrs]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileExplorer = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (index) => {
    if (filePreviews[index]) {
      URL.revokeObjectURL(filePreviews[index]);
    }
    setFiles(files.filter((_, i) => i !== index));
    setFilePreviews(filePreviews.filter((_, i) => i !== index));
  };

  // Categories
  useEffect(() => {
    dispatch(fetchCategoriesAction());
  }, [dispatch]);

  const { categories } = useSelector((state) => state?.categories || {});
  const categoriesList = categories?.categories || [];

  // Brands
  useEffect(() => {
    dispatch(fetchBrandsAction());
  }, [dispatch]);

  const { brands } = useSelector((state) => state?.brands || {});
  const brandsList = brands?.brands || [];

  // Styles
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [newStyleName, setNewStyleName] = useState("");
  const [addingStyle, setAddingStyle] = useState(false);

  const { styles } = useSelector((state) => state?.styles || {});
  const stylesList = styles?.styles || [];

  useEffect(() => {
    dispatch(fetchStylesAction());
  }, [dispatch]);

  const handleStyleChange = (selected) => {
    setSelectedStyle(selected);
  };

  const handleAddStyle = async () => {
    if (!newStyleName.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please enter a style name"
      });
      return;
    }

    setAddingStyle(true);

    try {
      const styleData = { name: newStyleName };
      const result = await dispatch(createStyleAction(styleData));

      if (result.type === 'styles/create/fulfilled') {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Style "${newStyleName}" added successfully`,
          timer: 1500,
          showConfirmButton: false,
        });
        setNewStyleName("");
        dispatch(fetchStylesAction());
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.payload?.message || "Failed to add style"
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Something went wrong"
      });
    } finally {
      setAddingStyle(false);
    }
  };

  const styleOptions = stylesList?.map((style) => ({
    value: style?._id,
    label: style?.name,
  })) || [];

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    brand: "",
    price: "",
    totalQty: "",
  });

  const handleOnChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const { isAdded, loading, error } = useSelector((state) => state?.products || {});

  // Reset isAdded flag when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetSuccessAction());
    };
  }, [dispatch]);

  // Track previous isAdded value to avoid multiple success modals
  const prevIsAdded = useRef(isAdded);

  useEffect(() => {
    // Only trigger when isAdded changes from false to true
    if (isAdded && !prevIsAdded.current) {
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Product added successfully",
        showConfirmButton: true,
        confirmButtonText: "Add Another Product",
        showCancelButton: true,
        cancelButtonText: "Go to Products",
      }).then((result) => {
        if (result.isConfirmed) {
          // Reset form to add another product
          setFormData({
            name: "",
            description: "",
            category: "",
            brand: "",
            price: "",
            totalQty: "",
          });
          setSelectedSize(null);
          setSelectedStyle(null);
          // Revoke object URLs to free memory
          filePreviews.forEach(preview => URL.revokeObjectURL(preview));
          setFiles([]);
          setFilePreviews([]);
          setFileErrs([]);
          // Reset isAdded flag in Redux for future submissions
          dispatch(resetSuccessAction());
        } else {
          // Go to products list
          navigate("/admin/manage-products");
        }
        setIsSubmitting(false);
      });
    }
    prevIsAdded.current = isAdded;
  }, [isAdded, dispatch, navigate]); // filePreviews removed from deps to avoid loops

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validation
    if (!formData.name.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Field", text: "Please enter product name" });
      return;
    }
    if (!formData.description.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Field", text: "Please enter product description" });
      return;
    }
    if (!formData.category) {
      Swal.fire({ icon: "warning", title: "Missing Field", text: "Please select a category" });
      return;
    }
    if (!formData.brand) {
      Swal.fire({ icon: "warning", title: "Missing Field", text: "Please select a brand" });
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      Swal.fire({ icon: "warning", title: "Invalid Price", text: "Please enter a valid price" });
      return;
    }
    if (!formData.totalQty || parseInt(formData.totalQty) <= 0) {
      Swal.fire({ icon: "warning", title: "Invalid Quantity", text: "Please enter a valid quantity" });
      return;
    }
   
    if (!selectedStyle) {
      Swal.fire({ icon: "warning", title: "Missing Style", text: "Please select a style" });
      return;
    }
    if (files.length === 0) {
      Swal.fire({ icon: "warning", title: "Missing Images", text: "Please upload at least one image" });
      return;
    }

    setIsSubmitting(true);

    // Create FormData
    const productData = new FormData();

    // Append text fields
    productData.append("name", formData.name);
    productData.append("description", formData.description);
    productData.append("category", formData.category);
    productData.append("brand", formData.brand);
    productData.append("price", formData.price);
    productData.append("totalQty", formData.totalQty);
    productData.append("style", selectedStyle?.label || "");
    productData.append("size", selectedSize.value); // Send as single string, not array

    // Append images
    files.forEach((file) => {
      productData.append("images", file);
    });

    // Debug: Log FormData contents
    console.log("=== Sending to Backend ===");
    for (let pair of productData.entries()) {
      if (pair[1] instanceof File) {
        console.log(`${pair[0]}: ${pair[1].name} (${pair[1].size} bytes)`);
      } else {
        console.log(`${pair[0]}: ${pair[1]}`);
      }
    }

    try {
      const result = await dispatch(createProductAction(productData));
      console.log("Dispatch result:", result);

      if (result.type === 'product/create/rejected') {
        console.error("Product creation failed:", result.payload);
        // Reset isAdded flag to allow future attempts to trigger success
        dispatch(resetSuccessAction());
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.payload?.message || "Failed to create product"
        });
        setIsSubmitting(false);
      }
      // If fulfilled, the useEffect will handle the success modal
    } catch (error) {
      console.error("Unexpected error:", error);
      dispatch(resetSuccessAction());
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {error && <ErrorMsg message={error?.message} />}
      {fileErrs?.length > 0 && fileErrs.map((err, idx) => (
        <ErrorMsg key={idx} message={err} />
      ))}

      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create New Product
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Add product details, images, size, and style
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-2xl">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleOnSubmit}>
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Product Name *
                </label>
                <div className="mt-1">
                  <input
                    name="name"
                    value={formData?.name}
                    onChange={handleOnChange}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Product Description *
                </label>
                <div className="mt-1">
                  <textarea
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleOnChange}
                    className="block w-full rounded-md border-gray-300 border shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    required
                  />
                </div>
              </div>

              {/* Category and Brand Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleOnChange}
                    className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm border"
                    required
                  >
                    <option value="">-- Select Category --</option>
                    {categoriesList?.map((category) => (
                      <option key={category?._id} value={category?._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Brand
                  </label>
                  <select
                    name="brand"
                    value={formData.brand}
                    onChange={handleOnChange}
                    className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm border"
                    required
                  >
                    <option value="">-- Select Brand --</option>
                    {brandsList?.map((brand) => (
                      <option key={brand?._id} value={brand?._id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Price and Quantity Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Price * (USD)
                  </label>
                  <div className="mt-1">
                    <input
                      name="price"
                      value={formData.price}
                      onChange={handleOnChange}
                      type="number"
                      step="0.01"
                      min="0"
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Total Quantity *
                  </label>
                  <div className="mt-1">
                    <input
                      name="totalQty"
                      value={formData.totalQty}
                      onChange={handleOnChange}
                      type="number"
                      min="0"
                      className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Size - Single Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Select Size *
                </label>
                <select
                  value={selectedSize?.value || ""}
                  onChange={(e) => {
                    const selected = sizeOptions.find(opt => opt.value === e.target.value);
                    setSelectedSize(selected || null);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm border"
                  required
                >
                  <option value="">-- Select Size --</option>
                  {sizeOptions.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
                {selectedSize && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ Selected size: <strong>{selectedSize.label}</strong>
                  </p>
                )}
              </div>

              {/* Style - Single Selection with Add New */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Select Style *
                </label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <Select
                      name="style"
                      options={styleOptions}
                      className="basic-single-select"
                      classNamePrefix="select"
                      isClearable={true}
                      isSearchable={true}
                      onChange={handleStyleChange}
                      value={selectedStyle}
                      placeholder="Select a style..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newStyleName}
                      onChange={(e) => setNewStyleName(e.target.value)}
                      placeholder="New style"
                      className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddStyle}
                      disabled={addingStyle}
                      className="inline-flex items-center rounded-md border border-transparent bg-orange-600 px-3 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {addingStyle ? "..." : "+"}
                    </button>
                  </div>
                </div>
                {selectedStyle && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ Selected style: <strong>{selectedStyle.label}</strong>
                  </p>
                )}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Product Images * (Max 5 images)
                </label>
                <div className="mt-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={fileHandleChange}
                    className="hidden"
                  />

                  <div className="flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <button
                          type="button"
                          onClick={openFileExplorer}
                          className="relative cursor-pointer rounded-md bg-white font-medium text-orange-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-orange-500 focus-within:ring-offset-2 hover:text-orange-500"
                        >
                          <span>Upload files</span>
                        </button>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 2MB each (max 5 images)
                      </p>
                      {files.length > 0 && (
                        <p className="text-xs text-green-600">
                          {files.length} / 5 images selected
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Image Previews */}
                  {filePreviews.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-4">
                      {filePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div>
                {loading ? (
                  <LoadingComponent />
                ) : (
                  <button
                    disabled={fileErrs?.length > 0 || isSubmitting}
                    type="submit"
                    className="flex w-full justify-center rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {isSubmitting ? "Adding Product..." : "Add Product"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}